import { readRecord, readString } from '@/lib/prop-utils';

export const escapeHtml = (value: string): string => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const BLOCK_LEVEL_NODE_TYPES = new Set([
  'paragraph',
  'heading',
  'blockquote',
  'listItem',
  'bulletList',
  'orderedList',
  'codeBlock',
  'horizontalRule',
]);

const readDocText = (node: unknown): string => {
  const record = readRecord(node);
  if (!record) {
    return '';
  }

  if (typeof record.text === 'string') {
    return record.text;
  }

  const children = Array.isArray(record.content) ? record.content : [];
  if (children.length === 0) {
    return '';
  }

  const childrenText = children.map((child) => readDocText(child)).join('');
  const nodeType = readString(record.type);
  if (!childrenText) {
    return '';
  }

  return BLOCK_LEVEL_NODE_TYPES.has(nodeType || '') ? `${childrenText}\n\n` : childrenText;
};

const buildHtmlFromPlainText = (plainText: string): string => {
  const normalized = plainText.replace(/\r\n/g, '\n').trim();
  if (!normalized) {
    return '';
  }

  return normalized
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br />')}</p>`)
    .join('');
};

const RICH_TEXT_BLOCK_TAGS = 'p|h[1-6]|blockquote|ul|ol|li';
const BLOCK_BOUNDARY_NEWLINES = new RegExp(
  `(</(?:${RICH_TEXT_BLOCK_TAGS})>)[ \\t]*[\\r\\n]+[ \\t]*(?=<(?:${RICH_TEXT_BLOCK_TAGS})\\b)`,
  'gi',
);

export const compactRichTextHtml = (html: string): string =>
  html.replace(BLOCK_BOUNDARY_NEWLINES, '$1').trim();

export const normalizeRichTextContent = (content: unknown): string => {
  if (typeof content === 'string') {
    return compactRichTextHtml(content);
  }

  const record = readRecord(content);
  if (!record) {
    return '';
  }

  const html =
    readString(record.html) ||
    readString(record.contentHtml) ||
    readString(record.renderedHtml) ||
    '';
  if (html.trim().length > 0) {
    return compactRichTextHtml(html);
  }

  const doc =
    (readString(record.type) === 'doc' ? record : undefined) ||
    readRecord(record.doc) ||
    readRecord(record.contentDoc);
  if (doc) {
    const docText = readDocText(doc)
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    const fromDoc = buildHtmlFromPlainText(docText);
    if (fromDoc) {
      return fromDoc;
    }
  }

  const plainText = readString(record.plainText) || readString(record.text) || '';
  return buildHtmlFromPlainText(plainText);
};
